import React, { useState, useEffect } from 'react';
import {
  BookmarkIcon,
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ShareIcon,
  StarIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  BeakerIcon,
  PencilIcon,
  CheckIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { FeedMixComponent, DailyNutritionalSummary } from '../api/nutrition';
import { feedMixTemplatesApi, FeedMixTemplate as ApiFeedMixTemplate } from '../api/feedMixTemplates';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface FeedMixTemplate extends ApiFeedMixTemplate {
  metadata: ApiFeedMixTemplate['metadata'] & {
    createdAt?: string;
    updatedAt?: string;
  };
}

interface FeedMixTemplateManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate?: (template: FeedMixTemplate) => void;
  selectedComponents?: Array<{ feedStockId: string; quantity: number }>;
}

export default function FeedMixTemplateManager({
  isOpen,
  onClose,
  onSelectTemplate,
  selectedComponents
}: FeedMixTemplateManagerProps) {
  const [templates, setTemplates] = useState<FeedMixTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<FeedMixTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'rating' | 'usage' | 'date'>('rating');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<FeedMixTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<FeedMixTemplate | null>(null);

  // Create template form
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    category: 'custom' as const,
    tags: [] as string[],
    isPublic: false,
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    filterAndSortTemplates();
  }, [templates, searchTerm, selectedCategory, sortBy]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await feedMixTemplatesApi.getAll();
      setTemplates(
        data.map((template) => ({
          ...template,
          metadata: {
            ...template.metadata,
            createdAt: template.createdAt,
            updatedAt: template.updatedAt,
          },
        })),
      );
    } catch (error) {
      console.error('Failed to load templates:', error);
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortTemplates = () => {
    let filtered = templates;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.metadata.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(template => template.category === selectedCategory);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'rating':
          return b.metadata.rating - a.metadata.rating;
        case 'usage':
          return b.metadata.useCount - a.metadata.useCount;
        case 'date':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        default:
          return 0;
      }
    });

    setFilteredTemplates(filtered);
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim()) {
      alert('Template name is required');
      return;
    }

    try {
      const totalQuantity = selectedComponents?.reduce((sum, item) => sum + item.quantity, 0) ?? 0;
      const components: FeedMixComponent[] = (selectedComponents ?? []).map((item) => ({
        feedStockId: item.feedStockId,
        feedStockName: item.feedStockId,
        quantity: item.quantity,
        unit: 'g',
        percentage: totalQuantity > 0 ? (item.quantity / totalQuantity) * 100 : 0,
        nutritionalContribution: {
          protein: 0,
          fat: 0,
          fiber: 0,
          carbohydrate: 0,
          moisture: 0,
        },
      }));

      const nutritionalSummary: DailyNutritionalSummary = {
        totalQuantity,
        totalCalories: 0,
        proteinPercentage: 0,
        fatPercentage: 0,
        fiberPercentage: 0,
        carbohydratePercentage: 0,
        moisturePercentage: 0,
        proteinGrams: 0,
        fatGrams: 0,
        fiberGrams: 0,
        carbohydrateGrams: 0,
        moistureGrams: 0,
        qualityScore: 0,
        recommendations: [],
      };

      const created = await feedMixTemplatesApi.create({
        name: newTemplate.name.trim(),
        description: newTemplate.description,
        category: newTemplate.category,
        components,
        nutritionalSummary,
        metadata: {
          createdBy: 'Current User',
          tags: newTemplate.tags,
          isPublic: newTemplate.isPublic,
          notes: newTemplate.notes,
        },
      });

      setTemplates((prev) => [
        ...prev,
        {
          ...created,
          metadata: {
            ...created.metadata,
            createdAt: created.createdAt,
            updatedAt: created.updatedAt,
          },
        },
      ]);
      setShowCreateModal(false);
      setNewTemplate({
        name: '',
        description: '',
        category: 'custom',
        tags: [],
        isPublic: false,
        notes: '',
      });
    } catch (error) {
      console.error('Failed to create template:', error);
      alert('Failed to create template');
    }
  };

  const handleUseTemplate = async (template: FeedMixTemplate) => {
    try {
      const updated = await feedMixTemplatesApi.recordUse(template.id);
      setTemplates((prev) =>
        prev.map((item) =>
          item.id === template.id
            ? {
                ...updated,
                metadata: {
                  ...updated.metadata,
                  createdAt: updated.createdAt,
                  updatedAt: updated.updatedAt,
                },
              }
            : item,
        ),
      );
    } catch (error) {
      console.error('Failed to record template use:', error);
    }

    if (onSelectTemplate) {
      onSelectTemplate(template);
    }
    onClose();
  };

  const handleEditTemplate = (template: FeedMixTemplate) => {
    setEditingTemplate({ ...template });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTemplate) return;

    try {
      const updated = await feedMixTemplatesApi.update(editingTemplate.id, {
        name: editingTemplate.name,
        description: editingTemplate.description,
        category: editingTemplate.category,
        components: editingTemplate.components,
        nutritionalSummary: editingTemplate.nutritionalSummary,
        metadata: editingTemplate.metadata,
      });

      setTemplates((prev) =>
        prev.map((item) =>
          item.id === updated.id
            ? {
                ...updated,
                metadata: {
                  ...updated.metadata,
                  createdAt: updated.createdAt,
                  updatedAt: updated.updatedAt,
                },
              }
            : item,
        ),
      );

      setShowEditModal(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Failed to update template:', error);
      alert('Failed to update template');
    }
  };

  const handleDuplicateTemplate = async (template: FeedMixTemplate) => {
    try {
      const duplicated = await feedMixTemplatesApi.create({
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        components: template.components,
        nutritionalSummary: template.nutritionalSummary,
        metadata: {
          ...template.metadata,
          createdBy: 'Current User',
          useCount: 0,
          rating: 0,
        },
      });

      setTemplates((prev) => [
        ...prev,
        {
          ...duplicated,
          metadata: {
            ...duplicated.metadata,
            createdAt: duplicated.createdAt,
            updatedAt: duplicated.updatedAt,
          },
        },
      ]);
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      alert('Failed to duplicate template');
    }
  };

  const handleShareTemplate = async (template: FeedMixTemplate) => {
    try {
      // Create a shareable link or copy template data to clipboard
      const templateData = {
        name: template.name,
        description: template.description,
        category: template.category,
        components: template.components,
        nutritionalSummary: template.nutritionalSummary
      };

      await navigator.clipboard.writeText(JSON.stringify(templateData, null, 2));
      alert('Template data copied to clipboard!');
    } catch (error) {
      console.error('Failed to share template:', error);
      alert('Failed to copy template data');
    }
  };

  const handleDeleteTemplate = async (template: FeedMixTemplate) => {
    if (window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      try {
        await feedMixTemplatesApi.delete(template.id);
        setTemplates((prev) => prev.filter((item) => item.id !== template.id));
      } catch (error) {
        console.error('Failed to delete template:', error);
        alert('Failed to delete template');
      }
    }
  };

  const handleViewDetails = (template: FeedMixTemplate) => {
    setSelectedTemplate(template);
    setShowDetailsModal(true);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'breeding':
        return '🥚';
      case 'maintenance':
        return '🍽️';
      case 'molting':
        return '🪻';
      case 'juvenile':
        return '🐣';
      default:
        return '📋';
    }
  };

  return (
    <>
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-6xl">
      <BrandModalHeader title="Feed Mix Templates" onClose={onClose} />
      <BrandModalBody>
            {selectedComponents && selectedComponents.length > 0 && (
              <div className="mb-4 flex justify-end">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-primary text-sm inline-flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Save as Template
                  </button>
              </div>
            )}

            {/* Search and Filters */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center w-full rounded-md border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-cyan-500 focus-within:border-cyan-500">
                <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </span>
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent text-sm focus:outline-none focus:ring-0"
                />
              </div>

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="block w-full border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="all-1753461297314" value="all">All Categories</option>
                <option key="breeding-1753461297314" value="breeding">Breeding</option>
                <option key="maintenance-1753461297314" value="maintenance">Maintenance</option>
                <option key="molting-1753461297314" value="molting">Molting</option>
                <option key="juvenile-1753461297314" value="juvenile">Juvenile</option>
                <option key="custom-1753461297314" value="custom">Custom</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="block w-full border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="rating-1753461297314" value="rating">Sort by Rating</option>
                <option key="usage-1753461297314" value="usage">Sort by Usage</option>
                <option key="name-1753461297314" value="name">Sort by Name</option>
                <option key="date-1753461297314" value="date">Sort by Date</option>
              </select>
            </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                <span className="ml-2 text-sm text-slate-600">Loading templates...</span>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-12">
                <BeakerIcon className="mx-auto h-12 w-12 text-slate-400" />
                <h3 className="mt-2 text-sm font-medium text-slate-900">No templates found</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {searchTerm || selectedCategory !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Create your first feed mix template'
                  }
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          <span className="text-2xl mr-2">{getCategoryIcon(template.category)}</span>
                          <div>
                            <h4 className="text-lg font-medium text-slate-900">{template.name}</h4>
                            <p className="text-sm text-slate-500 capitalize">{template.category}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="flex items-center mr-2">
                            {[...Array(5)].map((_, i) => (
                              <StarIcon
                                key={i}
                                className={`h-4 w-4 ${
                                  i < Math.floor(template.metadata.rating)
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="ml-1 text-sm text-slate-600">{template.metadata.rating.toFixed(1)}</span>
                          </div>
                        </div>
                      </div>

                      {template.description && (
                        <p className="mt-2 text-sm text-slate-700">{template.description}</p>
                      )}

                      {/* Nutritional Summary */}
                      <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
                        <div className="text-center p-2 bg-slate-50/80 rounded">
                          <div className="font-medium text-slate-900">{template.nutritionalSummary.proteinPercentage}%</div>
                          <div className="text-slate-500">Protein</div>
                        </div>
                        <div className="text-center p-2 bg-slate-50/80 rounded">
                          <div className="font-medium text-slate-900">{template.nutritionalSummary.fatPercentage}%</div>
                          <div className="text-slate-500">Fat</div>
                        </div>
                        <div className="text-center p-2 bg-slate-50/80 rounded">
                          <div className="font-medium text-slate-900">{template.nutritionalSummary.qualityScore}/100</div>
                          <div className="text-slate-500">Quality</div>
                        </div>
                        <div className="text-center p-2 bg-slate-50/80 rounded">
                          <div className="font-medium text-slate-900">${template.metadata.costPerKg}</div>
                          <div className="text-slate-500">Per kg</div>
                        </div>
                      </div>

                      {/* Tags */}
                      {template.metadata.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {template.metadata.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Usage stats */}
                      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                        <span>Used {template.metadata.useCount} times</span>
                        <span>Updated {template.metadata.updatedAt}</span>
                      </div>

                      {/* Actions */}
                      <div className="mt-4 flex items-center space-x-2">
                        <button
                          onClick={() => handleUseTemplate(template)}
                          className="flex-1 bg-cyan-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-cyan-700"
                        >
                          Use Template
                        </button>
                        <button
                          onClick={() => handleViewDetails(template)}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50/80"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="p-2 text-slate-400 hover:text-cyan-600"
                          title="Edit Template"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicateTemplate(template)}
                          className="p-2 text-slate-400 hover:text-green-600"
                          title="Duplicate Template"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleShareTemplate(template)}
                          className="p-2 text-slate-400 hover:text-cyan-600"
                          title="Share Template"
                        >
                          <ShareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          className="p-2 text-slate-400 hover:text-red-600"
                          title="Delete Template"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
      </BrandModalBody>
    </BrandModal>

      {/* Create Template Modal */}
      <BrandModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        maxWidthClass="max-w-lg"
        zIndexClass="z-[60]"
      >
        <BrandModalHeader title="Create Template" onClose={() => setShowCreateModal(false)} />
        <BrandModalBody className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Template Name</label>
                    <input
                      type="text"
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="e.g., My Custom Mix"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">Description</label>
                    <textarea
                      value={newTemplate.description}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Describe the purpose and benefits of this mix"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">Category</label>
                    <select
                      value={newTemplate.category}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, category: e.target.value as any }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="custom-1753461297314" value="custom">Custom</option>
                      <option key="breeding-1753461297314" value="breeding">Breeding</option>
                      <option key="maintenance-1753461297314" value="maintenance">Maintenance</option>
                      <option key="molting-1753461297314" value="molting">Molting</option>
                      <option key="juvenile-1753461297314" value="juvenile">Juvenile</option>
                    </select>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newTemplate.isPublic}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, isPublic: e.target.checked }))}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-slate-900">
                      Make template public (visible to other users)
                    </label>
                  </div>
        </BrandModalBody>
        <BrandModalFooter>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTemplate}
                  disabled={!newTemplate.name.trim()}
                  className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Template
                </button>
        </BrandModalFooter>
      </BrandModal>

      {/* Edit Template Modal */}
      <BrandModal
        open={showEditModal && !!editingTemplate}
        onClose={() => setShowEditModal(false)}
        maxWidthClass="max-w-2xl"
        zIndexClass="z-[60]"
      >
        <BrandModalHeader title="Edit Template" onClose={() => setShowEditModal(false)} />
        <BrandModalBody className="space-y-4">
                {editingTemplate && (
                  <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Template Name</label>
                    <input
                      type="text"
                      value={editingTemplate.name}
                      onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, name: e.target.value } : null)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">Description</label>
                    <textarea
                      value={editingTemplate.description || ''}
                      onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, description: e.target.value } : null)}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">Category</label>
                    <select
                      value={editingTemplate.category}
                      onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, category: e.target.value as any } : null)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="custom-1753461297314" value="custom">Custom</option>
                      <option key="breeding-1753461297314" value="breeding">Breeding</option>
                      <option key="maintenance-1753461297314" value="maintenance">Maintenance</option>
                      <option key="molting-1753461297314" value="molting">Molting</option>
                      <option key="juvenile-1753461297314" value="juvenile">Juvenile</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={editingTemplate.metadata.tags.join(', ')}
                      onChange={(e) => setEditingTemplate(prev => prev ? {
                        ...prev,
                        metadata: {
                          ...prev.metadata,
                          tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                        }
                      } : null)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="e.g., breeding, high-protein, balanced"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">Notes</label>
                    <textarea
                      value={editingTemplate.metadata.notes || ''}
                      onChange={(e) => setEditingTemplate(prev => prev ? {
                        ...prev,
                        metadata: { ...prev.metadata, notes: e.target.value }
                      } : null)}
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Additional notes about this template"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingTemplate.metadata.isPublic}
                      onChange={(e) => setEditingTemplate(prev => prev ? {
                        ...prev,
                        metadata: { ...prev.metadata, isPublic: e.target.checked }
                      } : null)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-slate-900">
                      Make template public (visible to other users)
                    </label>
                  </div>
                  </>
                )}
        </BrandModalBody>
        <BrandModalFooter>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="btn btn-primary text-sm inline-flex items-center"
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Save Changes
                </button>
        </BrandModalFooter>
      </BrandModal>

      {/* Template Details Modal */}
      <BrandModal
        open={showDetailsModal && !!selectedTemplate}
        onClose={() => setShowDetailsModal(false)}
        maxWidthClass="max-w-4xl"
        zIndexClass="z-[60]"
      >
        <BrandModalHeader
          title={selectedTemplate ? (
            <span className="inline-flex items-center gap-2">
              <span className="text-2xl">{getCategoryIcon(selectedTemplate.category)}</span>
              {selectedTemplate.name}
            </span>
          ) : 'Template Details'}
          subtitle={selectedTemplate?.category}
          onClose={() => setShowDetailsModal(false)}
        />
        <BrandModalBody>
                {selectedTemplate && (
                  <>
                {selectedTemplate.description && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Description</h4>
                    <p className="text-sm text-slate-700">{selectedTemplate.description}</p>
                  </div>
                )}

                {/* Nutritional Summary */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-slate-900 mb-3">Nutritional Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50/80 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-slate-900">{selectedTemplate.nutritionalSummary.proteinPercentage}%</div>
                      <div className="text-sm text-slate-500">Protein</div>
                    </div>
                    <div className="bg-slate-50/80 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-slate-900">{selectedTemplate.nutritionalSummary.fatPercentage}%</div>
                      <div className="text-sm text-slate-500">Fat</div>
                    </div>
                    <div className="bg-slate-50/80 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-slate-900">{selectedTemplate.nutritionalSummary.fiberPercentage}%</div>
                      <div className="text-sm text-slate-500">Fiber</div>
                    </div>
                    <div className="bg-slate-50/80 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-slate-900">{selectedTemplate.nutritionalSummary.qualityScore}/100</div>
                      <div className="text-sm text-slate-500">Quality Score</div>
                    </div>
                  </div>
                </div>

                {/* Components */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-slate-900 mb-3">Feed Components</h4>
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-slate-50/80">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Component</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Quantity</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Percentage</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Protein</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Fat</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedTemplate.components.map((component, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                              {component.feedStockName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                              {component.quantity} {component.unit}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                              {component.percentage}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                              {component.nutritionalContribution.protein}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                              {component.nutritionalContribution.fat}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Template Info</h4>
                    <dl className="space-y-1">
                      <div className="flex justify-between">
                        <dt className="text-sm text-slate-500">Created by:</dt>
                        <dd className="text-sm text-slate-900">{selectedTemplate.metadata.createdBy}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-slate-500">Created:</dt>
                        <dd className="text-sm text-slate-900">{selectedTemplate.metadata.createdAt}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-slate-500">Updated:</dt>
                        <dd className="text-sm text-slate-900">{selectedTemplate.metadata.updatedAt}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-slate-500">Usage count:</dt>
                        <dd className="text-sm text-slate-900">{selectedTemplate.metadata.useCount} times</dd>
                      </div>
                    </dl>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Additional Details</h4>
                    <dl className="space-y-1">
                      <div className="flex justify-between">
                        <dt className="text-sm text-slate-500">Cost per kg:</dt>
                        <dd className="text-sm text-slate-900">${selectedTemplate.metadata.costPerKg}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-slate-500">Rating:</dt>
                        <dd className="text-sm text-slate-900">{selectedTemplate.metadata.rating.toFixed(1)}/5</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-slate-500">Public:</dt>
                        <dd className="text-sm text-slate-900">{selectedTemplate.metadata.isPublic ? 'Yes' : 'No'}</dd>
                      </div>
                      {selectedTemplate.metadata.targetBirdCount && (
                        <div className="flex justify-between">
                          <dt className="text-sm text-slate-500">Target birds:</dt>
                          <dd className="text-sm text-slate-900">{selectedTemplate.metadata.targetBirdCount}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>

                {/* Tags */}
                {selectedTemplate.metadata.tags.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.metadata.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedTemplate.metadata.notes && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Notes</h4>
                    <p className="text-sm text-slate-700 bg-slate-50/80 p-3 rounded-lg">{selectedTemplate.metadata.notes}</p>
                  </div>
                )}
                  </>
                )}
        </BrandModalBody>
        <BrandModalFooter>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="btn btn-secondary text-sm"
                >
                  Close
                </button>
                {selectedTemplate && (
                  <>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleEditTemplate(selectedTemplate);
                  }}
                  className="btn btn-secondary text-sm inline-flex items-center"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleUseTemplate(selectedTemplate)}
                  className="btn btn-primary text-sm"
                >
                  Use Template
                </button>
                  </>
                )}
        </BrandModalFooter>
      </BrandModal>
    </>
  );
} 